import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Performance() {
  const { user } = useAuth();
  const isManager = user.role === "Manager";
  const [reviews, setReviews] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ freelancer_id: "", project_id: "", quality_score: 4.5, on_time_score: 4.5, revision_count: 0, reviewer_notes: "" });

  const load = async () => {
    const { data } = await api.get("/performance"); setReviews(data);
    const p = await api.get("/projects"); setProjects(p.data);
    if (isManager) { const f = await api.get("/freelancers"); setFreelancers(f.data); }
  };
  useEffect(() => { load(); }, []);

  const fname = (fid) => freelancers.find((x) => x.id === fid)?.name || (user.linked_freelancer_id === fid ? user.name : "—");

  const leaderboard = () => {
    const agg = {};
    reviews.forEach((r) => {
      agg[r.freelancer_id] = agg[r.freelancer_id] || { q: 0, o: 0, n: 0 };
      agg[r.freelancer_id].q += r.quality_score; agg[r.freelancer_id].o += r.on_time_score; agg[r.freelancer_id].n += 1;
    });
    return Object.entries(agg).map(([fid, a]) => ({ fid, name: fname(fid), quality: (a.q / a.n).toFixed(2), on_time: (a.o / a.n).toFixed(2), reviews: a.n }))
      .sort((a, b) => b.quality - a.quality);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/performance", { ...form, quality_score: Number(form.quality_score), on_time_score: Number(form.on_time_score), revision_count: Number(form.revision_count) });
      setNewOpen(false); setForm({ freelancer_id: "", project_id: "", quality_score: 4.5, on_time_score: 4.5, revision_count: 0, reviewer_notes: "" });
      load(); toast.success("Review submitted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <PageHeader
        title="Performance"
        description={isManager ? "Scorecards, quality, and on-time delivery." : "Your scorecard and reviewer notes."}
        actions={isManager && <button data-testid="add-review-btn" onClick={() => setNewOpen(true)} className="bg-black text-white px-4 py-2 text-sm hard-shadow-hover">+ Add Review</button>}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {isManager && (
          <div className="border border-neutral-200 bg-white lg:col-span-1">
            <div className="px-4 py-3 border-b border-neutral-200 label-caps">Leaderboard</div>
            <table className="fms-table w-full" data-testid="performance-leaderboard">
              <thead><tr><th>#</th><th>Freelancer</th><th>Quality</th><th>On-Time</th><th>Reviews</th></tr></thead>
              <tbody>
                {leaderboard().map((r, i) => (
                  <tr key={r.fid}>
                    <td className="font-mono-tight text-neutral-500">{String(i + 1).padStart(2, "0")}</td>
                    <td>{r.name}</td>
                    <td className="font-mono-tight">{r.quality}</td>
                    <td className="font-mono-tight">{r.on_time}</td>
                    <td className="font-mono-tight">{r.reviews}</td>
                  </tr>
                ))}
                {leaderboard().length === 0 && <tr><td colSpan={5} className="text-center text-neutral-500 py-6">No reviews yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <div className={`border border-neutral-200 bg-white ${isManager ? "" : "lg:col-span-2"}`}>
          <div className="px-4 py-3 border-b border-neutral-200 label-caps">{isManager ? "All Reviews" : "Your Reviews"}</div>
          <div className="divide-y divide-neutral-200">
            {reviews.map((r) => (
              <div key={r.id} className="p-4">
                <div className="flex justify-between items-baseline">
                  <div className="font-medium">{fname(r.freelancer_id)}</div>
                  <div className="font-mono-tight text-xs text-neutral-500">{formatDate(r.review_date)}</div>
                </div>
                <div className="mt-2 flex gap-4 font-mono-tight text-sm">
                  <span>Quality <b>{r.quality_score}</b></span>
                  <span>On-Time <b>{r.on_time_score}</b></span>
                  <span>Revisions <b>{r.revision_count}</b></span>
                </div>
                {r.reviewer_notes && <div className="text-sm text-neutral-700 mt-2">{r.reviewer_notes}</div>}
              </div>
            ))}
            {reviews.length === 0 && <div className="p-6 text-center text-neutral-500 text-sm">No reviews yet.</div>}
          </div>
        </div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none border-2 border-black">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add Review</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><label className="label-caps block mb-1">Freelancer</label>
              <select required value={form.freelancer_id} onChange={(e) => setForm({ ...form, freelancer_id: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                <option value="">Select</option>
                {freelancers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div><label className="label-caps block mb-1">Project (optional)</label>
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                <option value="">—</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="label-caps block mb-1">Quality</label><Input type="number" step="0.1" min="0" max="5" value={form.quality_score} onChange={(e) => setForm({ ...form, quality_score: e.target.value })} className="rounded-none border-black" /></div>
              <div><label className="label-caps block mb-1">On-Time</label><Input type="number" step="0.1" min="0" max="5" value={form.on_time_score} onChange={(e) => setForm({ ...form, on_time_score: e.target.value })} className="rounded-none border-black" /></div>
              <div><label className="label-caps block mb-1">Revisions</label><Input type="number" min="0" value={form.revision_count} onChange={(e) => setForm({ ...form, revision_count: e.target.value })} className="rounded-none border-black" /></div>
            </div>
            <div><label className="label-caps block mb-1">Notes</label><Textarea value={form.reviewer_notes} onChange={(e) => setForm({ ...form, reviewer_notes: e.target.value })} className="rounded-none border-black" /></div>
            <DialogFooter><button type="submit" data-testid="submit-review" className="bg-black text-white px-4 py-2 text-sm">Save Review</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
